import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useMediaQuery } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Code from "@mui/icons-material/Code";
import BarChart from "@mui/icons-material/BarChart";
import AttachMoney from "@mui/icons-material/AttachMoney";
import Share from "@mui/icons-material/Share";
import Group from "@mui/icons-material/Group"; // Icon for recruiters
import { saveTargetPath } from "../utils/storage";
import { Helmet } from "react-helmet";

function HomePage() {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isLoggedIn = useSelector(
    (state) => state.userReducer?.isLoggedIn || false
  );

  const handleUploadResumeClick = () => {
    saveTargetPath("/Upload-Resume");
  };
  const ResumeTitle = "Your CV, Reimagined.";
  const HomeMainTitle =
    "Transform Your Resume into an AI-Optimized, Recruiter-Ready Profile";
  const HomeSubTitle =
    "Recruiters scan resumes in under 8 seconds. Use our intelligent resume analyzer to make sure yours gets noticed and moves forward.";

  const baseURL = window.location.hostname.includes("aicv.trueselfy.com")
    ? process.env.REACT_APP_AICV_URL
    : process.env.REACT_APP_BASE_URL;

  // FeatureCard Component
  const FeatureCard = ({ icon, title, description }) => (
    <Paper
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#0F0D0E",
        borderRadius: "0.5rem",
        border: "1px solid #FDF9E0",
        transition: "transform 0.2s",
        "&:hover": { transform: "scale(1.02)" },
      }}
    >
      {icon}
      <Typography
        variant="h6"
        sx={{
          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
          fontWeight: "semibold",
          color: "#FDF9E0",
          mb: { xs: 1, sm: 2, md: 3 },
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "#FDF9E0",
          fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
        }}
      >
        {description}
      </Typography>
    </Paper>
  );

  // FeaturesSection
  const FeaturesSection = () => (
    <Box
      sx={{
        py: { xs: 4, sm: 6, md: 10 },
        backgroundColor: "#0A0B0F",
        borderTop: "1px solid #FDF9E0",
        mt: { xs: 4, md: 8 },
      }}
    >
      <Box sx={{ maxWidth: "80rem", mx: "auto", px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
              fontWeight: "bold",
              color: "#FDF9E0",
              mb: { xs: 1, sm: 2, md: 3 },
            }}
          >
            Why Use TrueSelfy?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1.125rem" },
              color: "#FDF9E0",
            }}
          >
            Optimize your resume with advanced AI tools designed for job seekers
            and recruiters.
          </Typography>
        </Box>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              icon={
                <Code
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.25rem" },
                    color: "#278C37",
                    mb: { xs: 1, sm: 2, md: 4 },
                  }}
                />
              }
              title="AI-Powered Resume Scanner"
              description="Automatically review and enhance your resume structure, keywords, and formatting for maximum impact."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              icon={
                <BarChart
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.25rem" },
                    color: "#278C37",
                    mb: { xs: 1, sm: 2, md: 4 },
                  }}
                />
              }
              title="Mobile-First Resume Format"
              description="Showcase your resume in a scrollable, visual layout that works seamlessly on any device — perfect for busy recruiters."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              icon={
                <AttachMoney
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.25rem" },
                    color: "#278C37",
                    mb: { xs: 1, sm: 2, md: 4 },
                  }}
                />
              }
              title="Quick Editing & Customization"
              description="Instantly update your work experience, skills, or projects with a few clicks."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              icon={
                <Share
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.25rem" },
                    color: "#278C37",
                    mb: { xs: 1, sm: 2, md: 4 },
                  }}
                />
              }
              title="Easy Sharing"
              description="Generate a unique resume link to share with recruiters, hiring managers, or post on LinkedIn."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              icon={
                <Group
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.25rem" },
                    color: "#278C37",
                    mb: { xs: 1, sm: 2, md: 4 },
                  }}
                />
              }
              title="Streamline Your Hiring Process"
              description="Glanceable Resume Overviews, Bulk Upload Resumes, Designed for Mobile Screening."
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  // TemplateCard Component
  const TemplateCard = ({ image, title, description }) => (
    <Card
      sx={{
        backgroundColor: "#0F0D0E",
        borderRadius: "0.5rem",
        overflow: "hidden",
        border: "1px solid #FDF9E0",
        transition: "transform 0.2s",
        "&:hover": { transform: "scale(1.02)" },
      }}
    >
      <CardMedia
        component="img"
        sx={{ objectFit: "cover" }}
        image={image}
        alt={title}
      />
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            fontWeight: "semibold",
            color: "#FDF9E0",
            mb: { xs: 0.5, sm: 1, md: 2 },
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#FDF9E0",
            fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
          }}
        >
          {description}
        </Typography>
      </Box>
    </Card>
  );

  // TemplatesSection
  const TemplatesSection = () => (
    <Box
      sx={{
        py: { xs: 4, sm: 6, md: 10 },
        backgroundColor: "#0D0E12",
      }}
    >
      <Box sx={{ maxWidth: "80rem", mx: "auto", px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
              fontWeight: "bold",
              color: "#FDF9E0",
              mb: { xs: 1, sm: 2, md: 3 },
            }}
          >
            Modern Templates Tailored for Tech Roles
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1.125rem" },
              color: "#FDF9E0",
            }}
          >
            Choose from tech-optimized resume templates for engineers,
            developers, and more
          </Typography>
        </Box>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TemplateCard
              image="../../assets/images/Resume.png"
              title="Modern Tech Template"
              description="Clean and professional template for software engineers"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TemplateCard
              image="../../assets/images/Resume2.png"
              title="Creative CV Template"
              description="Visually appealing template for designers and artists"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TemplateCard
              image="../../assets/images/Resume3.png"
              title="ATS-Friendly Template"
              description="Template optimized for Applicant Tracking Systems"
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  // TestimonialCard Component
  const TestimonialCard = ({ quote, author }) => (
    <Paper
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#0F0D0E",
        borderRadius: "0.5rem",
        textAlign: "center",
        border: "1px solid #FDF9E0",
        transition: "transform 0.2s",
        "&:hover": { transform: "scale(1.02)" },
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
          fontWeight: "medium",
          color: "#FDF9E0",
          mb: { xs: 1, sm: 2, md: 2 },
        }}
      >
        {quote}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "#FDF9E0",
          fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
        }}
      >
        — {author}
      </Typography>
    </Paper>
  );

  const ResumeAnalyzerSection = () => (
    <Box
      sx={{
        py: { xs: 4, sm: 6, md: 10 },
        backgroundColor: "#0A0B0F",
      }}
    >
      <Box
        sx={{
          maxWidth: "80rem",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          textAlign: "center",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
            fontWeight: "bold",
            color: "#FDF9E0",
            mb: { xs: 2, sm: 3, md: 4 },
          }}
        >
          What is a Resume Analyzer?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
            color: "#FDF9E0",
            maxWidth: "60rem",
            mx: "auto",
          }}
        >
          Our advanced resume analyzer evaluates your document for
          recruiter-readability, proper layout, and impactful content. Many
          resumes are skipped due to poor formatting or keyword gaps — we help
          you fix that fast.
        </Typography>
      </Box>
    </Box>
  );

  // TestimonialsSection
  const TestimonialsSection = () => (
    <Box
      sx={{
        py: { xs: 4, sm: 6, md: 10 },
        backgroundColor: "#0D0E12",
      }}
    >
      <Box sx={{ maxWidth: "80rem", mx: "auto", px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
              fontWeight: "bold",
              color: "#FDF9E0",
              mb: { xs: 1, sm: 2, md: 3 },
            }}
          >
            Trusted by Job Seekers Worldwide
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1.125rem" },
              color: "#FDF9E0",
            }}
          >
            Hear from users who transformed their job search with TrueSelfy.
          </Typography>
        </Box>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TestimonialCard
              quote="“My resume finally stood out and I got interview calls!”"
              author="Sonia R., UI/UX Designer"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TestimonialCard
              quote="“TrueSelfy helped me organize my resume for real-world recruiters and AI tools.”"
              author="Yash T., DevOps Engineer"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TestimonialCard
              quote="“TrueSelfy’s simple tweaks made my resume shine—I landed interviews in just days!”"
              author="Rohan S., Software Developer"
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  // StepCard Component
  const StepCard = ({ number, title, description }) => (
    <Paper
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#0F0D0E",
        borderRadius: "0.5rem",
        border: "1px solid #FDF9E0",
        transition: "transform 0.2s",
        "&:hover": { transform: "scale(1.02)" },
      }}
    >
      <Box
        sx={{
          width: { xs: 40, sm: 44, md: 48 },
          height: { xs: 40, sm: 44, md: 48 },
          backgroundColor: "#278C37",
          color: "#FDF9E0",
          borderRadius: "9999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
          fontWeight: "bold",
          mb: { xs: 2, sm: 2, md: 3 },
        }}
      >
        {number}
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
          fontWeight: "semibold",
          color: "#FDF9E0",
          mb: { xs: 1, sm: 1, md: 2 },
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "#FDF9E0",
          fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
        }}
      >
        {description}
      </Typography>
    </Paper>
  );

  // HowItWorksSection
  const HowItWorksSection = () => (
    <Box
      sx={{
        py: { xs: 4, sm: 6, md: 10 },
        backgroundColor: "#0A0B0F",
      }}
    >
      <Box sx={{ maxWidth: "80rem", mx: "auto", px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
              fontWeight: "bold",
              color: "#FDF9E0",
              mb: { xs: 1, sm: 2, md: 3 },
            }}
          >
            How It Works
          </Typography>
        </Box>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StepCard
              number="1"
              title="Upload Your Resume"
              description="Upload your resume in PDF, Word, or ZIP format."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StepCard
              number="2"
              title="AI Analysis & Customization"
              description="Our AI Resume Analyzer scans for improvements, and you can customize your profile in a visual format."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StepCard
              number="3"
              title="Share Your Profile"
              description="Generate a unique link to share your optimized resume instantly — no attachments required."
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  // CallToActionSection
  const CallToActionSection = () => (
    <Box
      sx={{
        background: "#0D0E12",
        py: { xs: 4, sm: 6, md: 10 },
        borderBottom: "1px solid #FDF9E0",
      }}
    >
      <Box
        sx={{
          maxWidth: "56rem",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          textAlign: "center",
          color: "#FDF9E0",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.25rem" },
            fontWeight: "bold",
            mb: { xs: 2, sm: 3, md: 3 },
          }}
        >
          Get Started with Smart Resume Analysis
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
            mb: { xs: 3, sm: 4, md: 4 },
            opacity: 0.9,
          }}
        >
          Don’t let your resume get overlooked. Use our AI resume builder to
          create a clean, professional profile that gets you closer to your
          dream job.
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#278C37",
            color: "#FDF9E0",
            px: { xs: 4, sm: 6, md: 8 },
            py: { xs: 1.5, sm: 2 },
            fontWeight: "medium",
            borderRadius: "9999px",
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            "&:hover": { backgroundColor: "#1f6e2b" },
          }}
          href="/Upload-Resume"
          onClick={handleUploadResumeClick}
        >
          Create Your AI Resume Now
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        minHeight: "100vh",
        width: "100%",
        color: "#fff",
        backgroundImage: "url('../../assets/images/homepage-background.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Helmet>
        <title>AI Resume Builder & Smart Resume Analyzer | TrueSelfy</title>
        <meta
          name="description"
          content="Create a recruiter-ready, AI-enhanced resume in minutes. TrueSelfy transforms your resume into a mobile-optimized, professional profile using our smart resume analyzer and visual resume builder."
        />
        <meta
          name="keywords"
          content="resume analyzer, AI resume builder, resume optimization, smart resume format, recruiter resume tool"
        />
        <meta
          property="og:title"
          content="AI Resume Builder & Smart Resume Analyzer | TrueSelfy"
        />
        <meta
          property="og:description"
          content="Transform your resume into a mobile-optimized, recruiter-ready format using AI. Share your profile in seconds with TrueSelfy."
        />
        <meta property="og:url" content="https://aicv.trueselfy.com/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://aicv.trueselfy.com/assets/share-image.png"
        />
      </Helmet>
      <Header />
      <Grid
        container
        spacing={{ xs: 2, sm: 4, md: 4 }}
        justifyContent="center"
        alignItems="center"
        sx={{
          pt: { xs: 6, sm: 8, md: 8 },
          pb: { xs: 4, sm: 4, md: 4 },
          px: { xs: 2, sm: 5, md: 5, lg: 10, xl: 15 },
          flexGrow: 1,
        }}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            mt: { xs: "108px", sm: "70px", md: "80px", lg: "80px", xl: "56px" },
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: { xs: "14px", md: "24px" },
              lineHeight: "100%",
              color: "#FDF9E0", // Apply theme text color
              mb: { xs: "12px", md: "20px" },
              textAlign: "left",
            }}
          >
            {ResumeTitle}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              mb: { xs: "12px", sm: "16px", md: "20px" },
              fontSize: { xs: "24px", sm: "36px", md: "55px" },
              lineHeight: { xs: "26px", sm: "40px", md: "65px" },
              fontFamily: "Kanit",
              color: "#FDF9E0",
              textAlign: "left",
            }}
          >
            {HomeMainTitle}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: { xs: "14px", md: "24px" },
              lineHeight: { xs: "16px", md: "34px" },
              mb: { xs: "12px", sm: "20px", md: "30px" },
              color: "#FDF9E0",
              textAlign: "left",
            }}
          >
            {HomeSubTitle}
          </Typography>
          <Grid
            item
            xs={12}
            sx={{
              textAlign: "center",
              flexWrap: "wrap",
              display: "flex",
              justifyContent: "left",
              gap: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#278C37",
                  color: "#FDF9E0",
                  borderRadius: { xs: "6px", sm: "8px", md: "10px" },
                  padding: { xs: "8px 14px", sm: "10px 20px", md: "15px 30px" },
                  cursor: "pointer",
                  fontSize: { xs: "14px", sm: "18px", md: "24px" },
                  lineHeight: "100%",
                  fontWeight: { xs: 500, md: 700 },
                  textTransform: "none",
                }}
                href="/Upload-Resume"
                onClick={handleUploadResumeClick}
              >
                Create Your AI Resume Now
              </Button>
              <Box
                component="img"
                src="../../assets/images/click-indicator.svg"
                alt="Indicator Icon"
                sx={{
                  height: "100%",
                  width: "100%",
                  position: "absolute",
                  bottom: { xs: "-7px", sm: "-10px", md: "-15px" },
                  right: { xs: "-4px", sm: "-10px", md: "-17px" },
                  maxHeight: { xs: "18px", sm: "30px", md: "44px" },
                  maxWidth: { xs: "18px", sm: "30px", md: "44px" },
                }}
              />
            </Box>
            <Box>
              <Button
                variant="contained"
                component="a"
                sx={{
                  backgroundColor: "transparent",
                  border: "1px solid #FDF9E0",
                  color: "#FDF9E0",
                  borderRadius: { xs: "6px", sm: "8px", md: "10px" },
                  padding: { xs: "8px 14px", sm: "10px 20px", md: "15px 30px" },
                  cursor: "pointer",
                  fontSize: { xs: "14px", sm: "18px", md: "24px" },
                  lineHeight: "100%",
                  fontWeight: { xs: 500, md: 700 },
                  textTransform: "none",
                }}
                // target="_blank"
                rel="noopener noreferrer"
                href={`${baseURL}/public-profile/sample-profile`}
              >
                View Sample Profile
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} sx={{ textAlign: "center" }}>
          <Box
            component="img"
            sx={{
              height: "100%",
              width: "100%",
              maxHeight: { xs: "186px", sm: "300px", md: "512px" },
              maxWidth: { xs: "186px", sm: "300px", md: "512px" },
            }}
            alt="Wallet Illustration"
            src="../../assets/images/Wallet-coin.svg"
          />
        </Grid>
      </Grid>
      <FeaturesSection />
      <TemplatesSection />
      <ResumeAnalyzerSection />
      <TestimonialsSection />
      <HowItWorksSection />
      <CallToActionSection />
      <Footer sx={{ mt: "auto" }} />
    </Box>
  );
}

export default HomePage;
